import { TableRow, TableCell, toCurrencyString, CurrencyCode, Spinner } from '@client/toolkit';
import React, { useCallback, useState } from 'react';
import { getColors } from '../../../../styles/color-tokens';
import { useLazyGetInvoicesByIdDocumentQuery, OverdueInvoiceDto } from '@client/api';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../hooks';
import { selectSelectedTenant } from '../../../../redux/session/session.slice';

export const InvoiceRow = ({ invoice }: { invoice: OverdueInvoiceDto }) => {
  const colors = getColors();
  const { t } = useTranslation('validation');
  const { t: tReporting } = useTranslation('reporting');
  const [getInvoicesByIdDocument] = useLazyGetInvoicesByIdDocumentQuery();
  const [isDownloading, setIsDownloading] = useState(false);

  // Check if ERP is configured for the selected tenant from session
  const selectedTenant = useAppSelector(selectSelectedTenant);
  const isErpConfigured = selectedTenant && selectedTenant !== 'all'
    ? !!selectedTenant.erpIntegrations?.some((erp) => erp.configured)
    : false;

  const handleOpenInvoice = useCallback(() => {
    setIsDownloading(true);
    getInvoicesByIdDocument({ id: invoice.invoiceId })
      .unwrap()
      .then((blobUrl) => {
        const link = document.createElement('a');
        link.href = blobUrl as string;
        link.download = invoice.invoiceId;
        document.body.appendChild(link);
        link.click();
      })
      .catch((e) => {
        toast.error(t(`server.FETCH_ERROR`), { id: 'global-error' });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }, [getInvoicesByIdDocument, invoice.invoiceId]);

  return (
    <TableRow className="bg-surface-base border-b border-surface-elevated">
      <TableCell className="px-2 py-2"></TableCell>
      <TableCell className="px-2 py-2"></TableCell>
      <TableCell className="px-2 py-2"></TableCell>
      <TableCell className="px-2 py-2 align-top">
        {toCurrencyString(invoice.amountOutstanding.value, '-', {
          currency: invoice.amountOutstanding.currency as CurrencyCode,
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        })}
      </TableCell>
      <TableCell className={`px-2 py-2 align-top ${isErpConfigured ? '!text-brand-primary underline' : ''}`}>
        {!isErpConfigured ? (
          <span className="!text-foreground-secondary">-</span>
        ) : isDownloading ? (
          <span className="inline-flex items-center gap-1">
            <Spinner size="xs" />
            {tReporting('downloading')}
          </span>
        ) : (
          <a onClick={handleOpenInvoice} className="cursor-pointer">
            #{invoice.invoiceNumber}
          </a>
        )}
      </TableCell>
      <TableCell className="px-2 py-2 align-top">{invoice.invoiceText}</TableCell>
      <TableCell className={`px-2 py-2 align-top !text-[${colors.status.error}]`}>{invoice.dueDate}</TableCell>
      <TableCell className={`px-2 py-2 align-top !text-[${colors.status.error}]`}>{invoice.daysOverdue}</TableCell>
    </TableRow>
  );
};
